import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EnquiryTable } from "./enquiries/EnquiryTable";
import { useStore } from "../helpers";
import {
    buildEnquiryRequestPayload,
    getEnquiryLoadError,
    normalizeEnquiryList,
} from "./enquiries/enquiry-api";
import { EnquiryContentState } from "./enquiries/EnquiryContentState";
import { EnquiryDetailDrawer } from "./enquiries/EnquiryDetailDrawer";
import { EnquiryHeader } from "./enquiries/EnquiryHeader";
import { EnquirySummary } from "./enquiries/EnquirySummary";
import { EnquiryToolbar } from "./enquiries/EnquiryToolbar";
import { filterEnquiries, getEnquiryStats, type EnquiryFormFilter } from "./enquiries/enquiry-filters";
import { DEFAULT_ENQUIRY_DATE_RANGE } from "./enquiries/enquiry-date-range";
import type { EnquiryDateRangeFilter } from "./enquiries/enquiry-date-range";
import { ENQUIRY_CONTAINER_CLASS, ENQUIRY_PAGE_CLASS } from "./enquiries/enquiry-settings";
import type { EnquiryRow } from "./enquiries/enquiry-helpers";

const Enquiries = observer(() => {
    const rootStore = useStore();
    const [data, setData] = useState<EnquiryRow[]>([]);
    // const [query, setQuery] = useState("");
    const [formFilter, setFormFilter] = useState<EnquiryFormFilter>("all");
    const [dateRange, setDateRange] = useState<EnquiryDateRangeFilter>(DEFAULT_ENQUIRY_DATE_RANGE);
    const [selectedRow, setSelectedRow] = useState<EnquiryRow | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [paginationData, setPaginationData] = useState({
        page: 1,
        page_size: 10,
        total_pages: 0,
        total_records: 0,
        total_services: 0,
        total_careers: 0,
        total_leads: 0,
        query: "",
    });

    const changePageSize = (size: number) => {
        setPaginationData((prev) => {
            return {
                ...prev,
                page_size: size,
                page: 1,
            };
        });
    };

    const changePageNumber = (page: number) => {
        setPaginationData((prev) => {
            return { ...prev, page: page };
        });
    };

    const setQuery = (query: string) => {
        setPaginationData((prev) => {
            return { ...prev, query: query };
        });
    };
    const loadEnquiries = useCallback(async () => {
        setIsLoading(true);
        setError("");
        try {
            const res = await rootStore.get_enquiries(
                buildEnquiryRequestPayload(
                    paginationData.query,
                    paginationData.page,
                    paginationData.page_size
                )
            );
            if (res.ok) {
                setData(normalizeEnquiryList(res.data));

                setPaginationData((prev) => ({
                    ...prev,
                    total_pages: res.data?.total_pages ?? 0,
                    total_records: res.data?.total_records ?? 0,
                    total_services: res.data?.total_services ?? 0,
                    total_careers: res.data?.total_careers ?? 0,
                    total_leads: res.data?.total_leads ?? 0,
                }));
            } else {
                setData([]);
                setError(getEnquiryLoadError(res.status, res.problem));
            }
        } catch {
            setData([]);
            setError("Could not load enquiries. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [paginationData.page, paginationData.page_size, paginationData.query, rootStore]);

    useEffect(() => {
        loadEnquiries();
    }, [loadEnquiries]);

    const filteredData = useMemo(
        () => filterEnquiries(data, paginationData.query, formFilter, dateRange),
        [data, paginationData.query, formFilter, dateRange]
    );
    const stats = useMemo(() => getEnquiryStats(data), [data]);

    const emptyMessage = data.length === 0 ? "No enquiries found yet." : "No enquiries match your filters.";

    return (
        <div className={ENQUIRY_PAGE_CLASS}>
            <div className={ENQUIRY_CONTAINER_CLASS}>
                <EnquiryHeader />

                {error ? (
                    <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                ) : null}

                <EnquirySummary
                    total={paginationData.total_records}
                    services={paginationData.total_services}
                    careers={paginationData.total_careers}
                    leads={paginationData.total_leads}
                    visible={filteredData.length}
                />

                <EnquiryToolbar
                    query={paginationData.query}
                    formFilter={formFilter}
                    dateRange={dateRange}
                    rows={filteredData}
                    onQueryChange={setQuery}
                    onFormFilterChange={setFormFilter}
                    onDateRangeChange={setDateRange}
                    onClearFilters={() => {
                        setQuery("");
                        setFormFilter("all");
                        setDateRange(DEFAULT_ENQUIRY_DATE_RANGE);
                    }}
                />

                {isLoading ? (
                    <EnquiryContentState variant="loading" message="Loading enquiries..." />
                ) : filteredData.length === 0 ? (
                    <EnquiryContentState variant="empty" message={emptyMessage} />
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
                        <div className="border-b border-gray-100 px-4 py-3 text-xs text-gray-500">
                            Click any row to view full enquiry details
                        </div>
                        <EnquiryTable
                            data={filteredData}
                            onRowClick={setSelectedRow}
                            currentPage={paginationData.page}
                            pageSize={paginationData.page_size}
                            setPageSize={changePageSize}
                            setPageNumber={changePageNumber}
                            totalPages={paginationData.total_pages}
                            totalRecords={paginationData.total_records}
                        />
                    </div>
                )}

                <EnquiryDetailDrawer
                    row={selectedRow}
                    onClose={() => setSelectedRow(null)}
                    refreshTable={() => loadEnquiries()}
                />
            </div>
        </div>
    );
});

export default Enquiries;
